import styled from 'styled-components';
import { RoundButton } from '../chatSurfaces.styled';

/**
 * The draft screen a picked image goes through before it is sent: the picture
 * on a dark backdrop, the tool circles under it, the caption bar at the bottom.
 * Same portal, same z-index and the same round white controls as the lightbox -
 * to the user this IS the lightbox, just before sending instead of after.
 */

export const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  /* The chat pages publish the VISIBLE height; on a phone the caption bar has
     to sit above the keyboard, and a plain inset: 0 would leave it under it. */
  height: var(--app-vh, 100dvh);
  /* The app's modals sit at 130; matching it keeps the layering predictable. */
  z-index: 130;
  /* Darker than the lightbox's 0.65: strokes and crop edges have to stay
     readable against whatever is behind, not just a photo look good. */
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 24px;

  @media (max-width: 800px) {
    padding: 12px;
    gap: 10px;
  }
`;

/** Top right, the same corner (and the same button) the lightbox closes from. */
export const CloseButton = styled(RoundButton)`
  position: absolute;
  top: 16px;
  right: 16px;
  /* Above the canvas box, which is a sibling in the stacking context. */
  z-index: 1;

  :disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

/** What is left between the close button and the controls; sized by flex so
 * the canvas can measure it and fit the picture inside. */
export const Stage = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

/**
 * Shrink-wraps the canvas so the crop shade, the busy overlay and the text
 * input all position against the picture itself, not the stage around it.
 * `overflow: hidden` is what clips the crop rectangle's giant spread shadow
 * to the picture's edges.
 *
 * Square corners, deliberately. A rounded preview curved the picture away
 * from under the crop frame's own square corners, so each handle sat over a
 * cut-off corner instead of on the edge it grabs - and the rounding was never
 * true anyway: what gets sent is a rectangle.
 */
export const CanvasBox = styled.div`
  position: relative;
  line-height: 0;
  overflow: hidden;
`;

export type EditorTool = 'crop' | 'draw' | 'text';

const TOOL_CURSOR: Record<EditorTool, string> = {
  crop: 'default',
  draw: 'crosshair',
  text: 'text',
};

export const EditCanvas = styled.canvas<{ $tool: EditorTool | null }>`
  display: block;
  /* Or the browser's pan gesture wins over drawing on touch. */
  touch-action: none;
  cursor: ${({ $tool }) => ($tool ? TOOL_CURSOR[$tool] : 'default')};
`;

/**
 * Stands in for the one text being typed, positioned over its spot on the
 * canvas. Its font MUST stay in step with `textFont` in imageEditorCore - the
 * committed text is drawn with that, and a mismatch makes the words jump the
 * moment typing ends.
 */
export const TextEditInput = styled.input`
  position: absolute;
  transform: translate(-50%, -50%);
  background: transparent;
  border: 1px dashed rgba(255, 255, 255, 0.7);
  border-radius: 8px;
  outline: none;
  padding: 2px 10px;
  text-align: center;
  font-family: 'SF Pro Display', sans-serif;
  font-weight: 600;
  caret-color: ${({ theme }) => theme.white};

  ::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

export const Controls = styled.div`
  flex: none;
  width: min(560px, 100%);
  display: flex;
  flex-direction: column;
  /* Stretch, so the colour pill and the caption bar run the full width; the
     tool row centres itself instead. */
  align-items: stretch;
  gap: 12px;
`;

/**
 * The colour picker: one wide pill, dragged like a scrubber. Its gradient is
 * generated next to the colour function in imageEditorCore and arrives as an
 * inline style, so this module stays a styled-components-only leaf.
 */
export const ColorTrack = styled.div`
  position: relative;
  width: 100%;
  height: 24px;
  border-radius: 12px;
  cursor: pointer;
  /* The drag IS the gesture here; without this, touch scrolls instead. */
  touch-action: none;
  /* A hairline ring, or the black end of the track dissolves into the
     backdrop and the pill looks bitten off. */
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.35);
`;

/** Filled with the picked colour by the component; never catches the pointer,
 * so the track under it sees the whole drag. */
export const ColorThumb = styled.div`
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 3px solid ${({ theme }) => theme.white};
  box-shadow: ${({ theme }) => theme.shadows.shadow1};
  pointer-events: none;
`;

/**
 * How thick the brush draws, on a wedge that shows the answer rather than
 * naming it - thin at one end, thick at the other, the way every drawing app
 * states this. No number: nobody picks a stroke width in pixels, they pick the
 * one that looks right on the picture in front of them.
 *
 * Same width and the same drag as the colour pill above it, so the two read as
 * one control with two rows.
 */
export const SizeTrack = styled.div`
  position: relative;
  width: 100%;
  height: 24px;
  cursor: pointer;
  /* The drag IS the gesture; without this, touch scrolls the page instead. */
  touch-action: none;

  /* The wedge itself. Inset by half the thumb's slot at each end so the track
     starts and stops where the thumb can actually reach. */
  ::before {
    content: '';
    position: absolute;
    left: 13px;
    right: 13px;
    top: 50%;
    transform: translateY(-50%);
    height: 18px;
    background: rgba(255, 255, 255, 0.35);
    clip-path: polygon(0 44%, 100% 0, 100% 100%, 0 56%);
  }
`;

/**
 * The picked size, drawn at (near enough) the size it means - so the control
 * previews the mark instead of describing it. Filled with the active colour,
 * which is the other thing about to be drawn.
 *
 * Sized inline by the component; positioned in a fixed 26px slot regardless, so
 * a small thumb sits centred in that slot rather than sliding past the end of
 * the track.
 */
export const SizeThumb = styled.div`
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  border: 2px solid ${({ theme }) => theme.white};
  box-shadow: ${({ theme }) => theme.shadows.shadow1};
  pointer-events: none;
`;

export const ToolRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

/** Between the tools and the history controls - two groups, one row. */
export const ToolDivider = styled.div`
  width: 1px;
  height: 24px;
  margin: 0 4px;
  background: rgba(255, 255, 255, 0.35);
`;

/**
 * A tool circle. The active tool fills in red exactly the way the send disc
 * does - pressed state is a filled disc everywhere in this feature. The icons
 * are all on the Font Awesome 640 grid, so one pinned size renders them
 * optically equal.
 */
export const ToolButton = styled(RoundButton)<{ $active?: boolean }>`
  width: 40px;
  height: 40px;
  background: ${({ theme, $active }) => ($active ? theme.primary : theme.white)};
  transition: ${({ theme }) => theme.transitions.ease200ms};

  svg {
    width: 16px;
    height: 16px;
  }

  :disabled {
    opacity: 0.4;
    cursor: default;
  }

  @media (max-width: 800px) {
    width: 36px;
    height: 36px;
  }
`;
